/**
 * Layout store — état persisté des panneaux + mémoire par contexte.
 *
 * @remarks
 * État strictement persistable (cf. `use-ui-ephemeral-store.ts` pour le drag,
 * hover, et autres états transitoires).
 *
 * Le pattern "mémoire par contexte" reprend l'idée IDE-like du legacy : chaque
 * contexte applicatif (skill-up-session, settings…) se souvient de son layout
 * indépendamment.
 *
 * - **Premier chargement** : pas d'entrée dans `contextPanelStates` → le shell
 *   applique les `panelRequirements` du manifest.
 * - **Changement de contexte** : on sauvegarde l'état courant dans
 *   `contextPanelStates[oldCtx]`, puis on restaure depuis
 *   `contextPanelStates[newCtx]`.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { CONTEXTS, type Context } from '@/app/registry';

type PanelSide = 'left' | 'right';

/**
 * Snapshot d'un contexte (sauvegardé puis restauré).
 */
interface ContextPanelSnapshot {
  readonly leftDrawerOpen: boolean;
  readonly rightDrawerOpen: boolean;
  readonly leftActiveTab: string | null;
  readonly rightActiveTab: string | null;
  /** Override du `defaultSide` du manifest, par drag-and-drop. */
  readonly tabPanelAssignments: Readonly<Record<string, PanelSide>>;
  /** Ordre custom des tabs dans chaque panneau (drag-and-drop intra-panel). */
  readonly tabOrder: { readonly left: readonly string[]; readonly right: readonly string[] };
}

interface LayoutState {
  // ── État courant (rendu par le shell) ─────────────────────────────────────
  leftDrawerOpen: boolean;
  rightDrawerOpen: boolean;
  leftActiveTab: string | null;
  rightActiveTab: string | null;
  tabPanelAssignments: Record<string, PanelSide>;
  tabOrder: { left: string[]; right: string[] };

  // ── Mémoire par contexte ──────────────────────────────────────────────────
  contextPanelStates: Partial<Record<Context, ContextPanelSnapshot>>;
  visitedContexts: Context[];

  // ── Actions de base ───────────────────────────────────────────────────────
  setLeftDrawerOpen: (open: boolean) => void;
  setRightDrawerOpen: (open: boolean) => void;
  toggleLeftDrawer: () => void;
  toggleRightDrawer: () => void;
  setLeftActiveTab: (id: string | null) => void;
  setRightActiveTab: (id: string | null) => void;

  // ── Actions context-aware ─────────────────────────────────────────────────
  saveContextState: (ctx: Context) => void;
  restoreContextState: (ctx: Context) => boolean;
  hasVisitedContext: (ctx: Context) => boolean;
  moveTabToPanel: (tabId: string, target: PanelSide) => void;
  reorderTabsInPanel: (panel: PanelSide, newOrder: string[]) => void;

  /** @internal Test helper. */
  _reset: () => void;
}

const INITIAL_STATE = {
  leftDrawerOpen: true,
  rightDrawerOpen: true,
  leftActiveTab: null as string | null,
  rightActiveTab: null as string | null,
  tabPanelAssignments: {} as Record<string, PanelSide>,
  tabOrder: { left: [] as string[], right: [] as string[] },
  contextPanelStates: {} as Partial<Record<Context, ContextPanelSnapshot>>,
  visitedContexts: [] as Context[],
};

export const useLayoutStore = create<LayoutState>()(
  devtools(
    persist(
      (set, get) => ({
        ...INITIAL_STATE,

        setLeftDrawerOpen: (open) => {
          set({ leftDrawerOpen: open });
        },
        setRightDrawerOpen: (open) => {
          set({ rightDrawerOpen: open });
        },
        toggleLeftDrawer: () => {
          set((s) => ({ leftDrawerOpen: !s.leftDrawerOpen }));
        },
        toggleRightDrawer: () => {
          set((s) => ({ rightDrawerOpen: !s.rightDrawerOpen }));
        },
        setLeftActiveTab: (id) => {
          set({ leftActiveTab: id });
        },
        setRightActiveTab: (id) => {
          set({ rightActiveTab: id });
        },

        saveContextState: (ctx) => {
          const s = get();
          const snapshot: ContextPanelSnapshot = {
            leftDrawerOpen: s.leftDrawerOpen,
            rightDrawerOpen: s.rightDrawerOpen,
            leftActiveTab: s.leftActiveTab,
            rightActiveTab: s.rightActiveTab,
            tabPanelAssignments: { ...s.tabPanelAssignments },
            tabOrder: { left: [...s.tabOrder.left], right: [...s.tabOrder.right] },
          };
          set((current) => ({
            contextPanelStates: { ...current.contextPanelStates, [ctx]: snapshot },
            visitedContexts: current.visitedContexts.includes(ctx)
              ? current.visitedContexts
              : [...current.visitedContexts, ctx],
          }));
        },

        restoreContextState: (ctx) => {
          const snapshot = get().contextPanelStates[ctx];
          if (!snapshot) return false;
          set({
            leftDrawerOpen: snapshot.leftDrawerOpen,
            rightDrawerOpen: snapshot.rightDrawerOpen,
            leftActiveTab: snapshot.leftActiveTab,
            rightActiveTab: snapshot.rightActiveTab,
            tabPanelAssignments: { ...snapshot.tabPanelAssignments },
            tabOrder: {
              left: [...snapshot.tabOrder.left],
              right: [...snapshot.tabOrder.right],
            },
          });
          return true;
        },

        hasVisitedContext: (ctx) => get().visitedContexts.includes(ctx),

        moveTabToPanel: (tabId, target) => {
          set((s) => ({
            tabPanelAssignments: { ...s.tabPanelAssignments, [tabId]: target },
          }));
        },

        reorderTabsInPanel: (panel, newOrder) => {
          set((s) => ({
            tabOrder: { ...s.tabOrder, [panel]: [...newOrder] },
          }));
        },

        _reset: () => {
          set({ ...INITIAL_STATE });
        },
      }),
      {
        name: 'saas-front-v2:layout',
        version: 3,
        partialize: (state) => ({
          // ⚠️ Ne JAMAIS persister les actions ni les fields purement runtime.
          // Si un nouveau field "transitoire" est ajouté, le filtrer ici.
          leftDrawerOpen: state.leftDrawerOpen,
          rightDrawerOpen: state.rightDrawerOpen,
          leftActiveTab: state.leftActiveTab,
          rightActiveTab: state.rightActiveTab,
          tabPanelAssignments: state.tabPanelAssignments,
          tabOrder: state.tabOrder,
          contextPanelStates: state.contextPanelStates,
          visitedContexts: state.visitedContexts,
        }),
        /**
         * **Migration policy** : à chaque changement de shape, bumper `version`
         * et ajouter une branche `if (oldVersion < N)` qui transforme l'ancien
         * payload vers la nouvelle shape. Si la migration échoue ou si l'ancien
         * payload est inattendu, on retourne `INITIAL_STATE` plutôt que de
         * crasher l'app de l'utilisateur.
         */
        migrate: (persistedState: unknown, oldVersion: number) => {
          try {
            // Version antérieure inconnue (< 1) → repart sur l'état initial (safe).
            if (oldVersion < 1) return INITIAL_STATE;

            let state = persistedState as Partial<LayoutState>;

            /**
             * Force `rightDrawerOpen: false` dans les snapshots persistés des
             * contextes donnés (le reste de leur layout est préservé). Sert à
             * répercuter un changement de « panneau droit fermé par défaut » sur
             * les utilisateurs ayant déjà visité ces pages — sinon leur état
             * mémorisé par contexte serait restauré tel quel.
             */
            const forceRightClosed = (contexts: Context[]): void => {
              if (!state.contextPanelStates) return;
              const nextStates = { ...state.contextPanelStates };
              for (const ctx of contexts) {
                const snap = nextStates[ctx];
                if (snap) nextStates[ctx] = { ...snap, rightDrawerOpen: false };
              }
              state = { ...state, contextPanelStates: nextStates };
            };

            // v1 → v2 : panneau droit fermé par défaut sur les contextes tests.
            if (oldVersion < 2) {
              forceRightClosed([
                CONTEXTS.TECH_TESTS_CATALOG,
                CONTEXTS.TECH_TESTS,
                CONTEXTS.CANDIDATE_RESULTS,
              ]);
            }

            // v2 → v3 : le form skill n'ouvre plus le panneau droit par défaut
            // (il s'ouvre uniquement pendant l'ajout/édition d'un palier).
            if (oldVersion < 3) {
              forceRightClosed([CONTEXTS.ADMIN_SKILL_FORM]);
            }

            return state;
          } catch {
            return INITIAL_STATE;
          }
        },
      },
    ),
    { name: 'layout', enabled: import.meta.env.DEV },
  ),
);
